import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const SMOOTH_DURATION = 400;
const SMOOTH_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (this.hasAttribute('data-smooth') && !reducedMotion.matches) {
      event.preventDefault();
      this.#toggleSmooth();
    }
  };

  /** @type {Animation | null} */
  #animation = null;

  /**
   * Opens/closes the row by animating the content height, so it eases
   * gently in every browser (CSS height transitions only work in Chrome).
   */
  #toggleSmooth() {
    const { details } = this;
    const content = details.querySelector(':scope > .details-content');
    if (!(content instanceof HTMLElement)) {
      details.open = !details.open;
      return;
    }

    const closing = details.open && !details.classList.contains('is-closing');
    const startHeight = details.open ? content.offsetHeight : 0;

    this.#animation?.cancel();
    details.classList.toggle('is-closing', closing);
    if (!closing) {
      this.#closeSiblings();
      details.open = true;
    }

    const { paddingTop, paddingBottom } = getComputedStyle(content);
    const shown = { height: `${content.scrollHeight}px`, paddingTop, paddingBottom, opacity: 1 };
    const hidden = { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };
    const current = { ...shown, height: `${startHeight}px`, opacity: startHeight ? 1 : 0 };
    content.style.overflow = 'hidden';

    this.#animation = content.animate(
      closing ? [current, hidden] : [startHeight ? current : hidden, shown],
      { duration: SMOOTH_DURATION, easing: SMOOTH_EASING }
    );

    this.#animation.onfinish = () => {
      if (closing) details.open = false;
      details.classList.remove('is-closing');
      content.style.overflow = '';
      this.#animation = null;
      if (this.#groupName) {
        details.setAttribute('name', this.#groupName);
        this.#groupName = '';
      }
    };
  }

  /** @type {string} */
  #groupName = '';

  /**
   * One-open-at-a-time groups (<details name="...">): the browser would snap
   * the other row shut instantly, so close it smoothly instead. Its name is
   * lifted until it has finished closing so the browser doesn't intervene.
   */
  #closeSiblings() {
    const name = this.details.getAttribute('name') || this.#groupName;
    if (!name) return;

    for (const other of document.querySelectorAll(`details[name="${CSS.escape(name)}"][open]`)) {
      const accordion = other.closest('accordion-custom');
      if (other === this.details || !(accordion instanceof AccordionCustom)) continue;
      if (other.classList.contains('is-closing')) continue;

      accordion.#groupName = name;
      other.removeAttribute('name');
      accordion.#toggleSmooth();
    }
  }

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
